/**
 * Indexer graph view (epic-6 §5 / IDX-5) — renders the `GET /indexer/graph`
 * response as a hierarchical SVG layout (sources → tables → stats). No new
 * dependencies: BFS layering + hand-rolled SVG.
 */
import { useMemo } from 'react';
import type { IndexerGraph, IndexerGraphNode } from '@/types';

const COLUMN_GAP = 180;
const ROW_GAP = 64;
const NODE_W = 150;
const NODE_H = 44;

const NODE_COLORS: Record<IndexerGraphNode['type'], string> = {
  source: '#3b82f6',
  table: '#8b5cf6',
  flow: '#059669',
  collection: '#0ea5e9',
  repo: '#6366f1',
};

interface PositionedNode extends IndexerGraphNode {
  x: number;
  y: number;
}

interface GraphViewProps {
  graph?: IndexerGraph;
  loading?: boolean;
  onSelectNode?: (node: IndexerGraphNode) => void;
}

export function GraphView({ graph, loading, onSelectNode }: GraphViewProps) {
  const { nodes, edges, width, height } = useMemo(() => {
    if (!graph) return { nodes: [], edges: [], width: 0, height: 0 };
    return layoutGraph(graph);
  }, [graph]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        Loading graph…
      </div>
    );
  }
  if (!graph || nodes.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        No indexer graph available — sync a source first.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-md border bg-card">
      <svg width={width} height={height} className="min-w-[640px]">
        {/* Edges */}
        {edges.map((e, i) => {
          const from = nodes.find((n) => n.id === e.from);
          const to = nodes.find((n) => n.id === e.to);
          if (!from || !to) return null;
          const x1 = from.x + NODE_W;
          const y1 = from.y + NODE_H / 2;
          const x2 = to.x;
          const y2 = to.y + NODE_H / 2;
          const mx = (x1 + x2) / 2;
          return (
            <path
              key={`e-${i}`}
              d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
              fill="none"
              stroke="currentColor"
              strokeOpacity={0.35}
              strokeWidth={1.2}
              strokeDasharray={e.kind === 'derived' ? '4 3' : undefined}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((n) => {
          const color = NODE_COLORS[n.type] ?? '#64748b';
          const stats = [
            n.stats?.chunks ? `${n.stats.chunks} chunks` : null,
            n.stats?.files ? `${n.stats.files} files` : null,
            n.stats?.lastSync ? 'synced' : null,
          ].filter(Boolean);
          return (
            <g
              key={n.id}
              transform={`translate(${n.x}, ${n.y})`}
              className="cursor-pointer"
              onClick={() => onSelectNode?.(n)}
            >
              <rect
                width={NODE_W}
                height={NODE_H}
                rx={8}
                fill={color}
                fillOpacity={0.1}
                stroke={color}
                strokeWidth={1.4}
              />
              <text
                x={8}
                y={18}
                fontSize={11}
                fontWeight={600}
                fill="currentColor"
                className="select-none"
              >
                {n.label.length > 26 ? `${n.label.slice(0, 25)}…` : n.label}
              </text>
              <text x={8} y={32} fontSize={9} fill="currentColor" fillOpacity={0.6} className="select-none">
                {n.type}
                {stats.length > 0 ? ` · ${stats.join(', ')}` : ''}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Pure layout logic (unit-testable) ────────────────────────────────────────

export interface LayoutResult {
  nodes: PositionedNode[];
  edges: IndexerGraph['edges'];
  width: number;
  height: number;
}

export function layoutGraph(graph: IndexerGraph): LayoutResult {
  const { nodes: rawNodes, edges } = graph;

  // Layer each node via BFS (bounded — cyclic graphs can't be layered).
  const byId = new Map(rawNodes.map((n) => [n.id, n]));
  const incoming = new Map<string, number>();
  const children = new Map<string, string[]>();
  for (const n of rawNodes) incoming.set(n.id, 0);
  for (const e of edges) {
    if (!byId.has(e.from) || !byId.has(e.to)) continue;
    incoming.set(e.to, (incoming.get(e.to) ?? 0) + 1);
    const list = children.get(e.from) ?? [];
    list.push(e.to);
    children.set(e.from, list);
  }

  const layer = new Map<string, number>();
  const roots = rawNodes.filter((n) => (incoming.get(n.id) ?? 0) === 0);
  const queue: string[] = roots.length > 0 ? roots.map((n) => n.id) : rawNodes.slice(0, 4).map((n) => n.id);
  for (const id of queue) layer.set(id, 0);

  for (let qi = 0; qi < queue.length; qi++) {
    const id = queue[qi];
    const l = layer.get(id) ?? 0;
    for (const child of children.get(id) ?? []) {
      if (!layer.has(child)) {
        layer.set(child, l + 1);
        queue.push(child);
      } else {
        layer.set(child, Math.max(layer.get(child)!, l + 1));
      }
    }
  }

  // Assign orphaned nodes to layer 0 at the tail.
  const maxLayer = rawNodes.reduce((acc, n) => Math.max(acc, layer.get(n.id) ?? 0), 0);
  for (const n of rawNodes) {
    if (!layer.has(n.id)) layer.set(n.id, maxLayer);
  }

  // Position: column = layer, row = insertion order within column.
  const perLayer = new Map<number, IndexerGraphNode[]>();
  for (const n of rawNodes) {
    const l = layer.get(n.id) ?? 0;
    const list = perLayer.get(l) ?? [];
    list.push(n);
    perLayer.set(l, list);
  }

  const columnWidth = NODE_W + COLUMN_GAP;
  const nodes: PositionedNode[] = [];
  for (const [lStr, list] of perLayer) {
    list.forEach((n, row) => {
      nodes.push({
        ...n,
        x: Number(lStr) * columnWidth + 8,
        y: row * (NODE_H + ROW_GAP) + 12,
      });
    });
  }

  const maxCols = perLayer.size;
  const maxRows = Math.max(...[...perLayer.values()].map((l) => l.length), 1);
  return {
    nodes,
    edges,
    width: Math.max(maxCols, 1) * columnWidth + NODE_W,
    height: maxRows * (NODE_H + ROW_GAP) + 24,
  };
}